import React from 'react';

import {Button, ButtonToolbar, Icon, Menu, MenuItem, Space, List, ListItem, Title, Text, Modal} from '@mc/ui-v4';

import {mcExport, mcClose, mcMenuKebab} from '@mc/react-ui-icons';
import {ExportFormat, ExportImportApi} from '../../../api';
import {actionApiCall} from '@mc/react-app-v4';
import {ApiCallWaitingModal} from '../../modals/ApiCallWaitingModal';
import {SERVER_CONTEXT_PATH} from '../../../const';
import {FeatureFilterType, FeatureId, MapEditorActions, LayerId} from '@mc/react-mapio';
import {ExportFormat as MapioExportFormat} from '../../../enums/ExportFormats';

type Props = {
    formats: any[];
    /** Export do XLS – spouští standardní mechanismus FinalTable místo exportu přes GDAL. */
    onExportXlsx: () => Promise<void>;
    calcQueryIds: FeatureId[];
    layerId: LayerId;
    /** LayerId diskriminované vrstvy – pokud je nastaveno, použije se místo layerId pro setLayerFeaturesFilter */
    discriminatedLayerId?: LayerId;
    setIsExtentChecked: React.Dispatch<React.SetStateAction<boolean>>;
    isExtentChecked: boolean;
    setIsProvazatChecked: React.Dispatch<React.SetStateAction<boolean>>;
    isProvazatChecked: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    mapRef: React.MutableRefObject<MapEditorActions>;
};

export const AttributeTableToolbar: React.FC<Props> = ({
    formats,
    onExportXlsx,
    calcQueryIds,
    layerId,
    discriminatedLayerId,
    setIsExtentChecked,
    isExtentChecked,
    setIsProvazatChecked,
    isProvazatChecked,
    setIsOpen,
    mapRef,
}) => {
    /** ID vrstvy, které se použije pro filter – diskriminovaná vrstva má přednost */
    const filterLayerId = discriminatedLayerId ?? layerId;

    return (
        <ButtonToolbar>
            <Button
                icon={<Icon icon={mcExport} />}
                style={'link'}
                dropdown
                menu={
                    <Menu className={'m-1'}>
                        <Title className={'mb-1'}>Export</Title>
                        <Text type={'block'}>{'Vyberte formát exportu'}</Text>
                        {formats?.map(format => {
                            return (
                                <MenuItem
                                    key={format.id}
                                    text={`${format.name}`}
                                    onClick={async () => {
                                        // XLS(X) se exportuje standardním mechanismem FinalTable, ostatní formáty přes GDAL
                                        if (format.id === MapioExportFormat.xlsx) {
                                            await onExportXlsx();
                                            return;
                                        }

                                        await Modal.showWaiting(
                                            actionApiCall(async () => {
                                                let res = (
                                                    await ExportImportApi._export(
                                                        Number(layerId),
                                                        format.id.toUpperCase() as ExportFormat,
                                                        calcQueryIds as string[],
                                                    )
                                                ).data;

                                                window.open(SERVER_CONTEXT_PATH + res, '_self');
                                            }),
                                            ApiCallWaitingModal,
                                        );
                                    }}
                                />
                            );
                        })}
                    </Menu>
                }
                title={'Exportovat tabulku'}
            />
            <Button
                icon={<Icon icon={mcMenuKebab} />}
                style={'link'}
                menu={
                    <Menu className={'m-1'}>
                        <Space vertical>
                            <Title type={'card'}>Nastavení tabulky</Title>
                            <List mode={'input'}>
                                <ListItem
                                    checkable
                                    checked={isExtentChecked}
                                    title={'Zobrazit jen objekty ve výřezu mapy'}
                                    rowCheck
                                    onCheck={() => {
                                        setIsExtentChecked(!isExtentChecked);
                                        mapRef.current.setLayerFeaturesFilter(FeatureFilterType.SHOW_EXTENT, filterLayerId);
                                    }}
                                >
                                    V tabulce se zobrazí pouze objekty, které jsou ve výřezu obrazovky.
                                </ListItem>
                                <ListItem
                                    checkable
                                    checked={isProvazatChecked}
                                    title={'Svázat výběr v mapě a v tabulce'}
                                    rowCheck
                                    onCheck={() => setIsProvazatChecked(!isProvazatChecked)}
                                >
                                    Pokud v mapě vyberete nějaké objekty, označí se v tabulce a naopak.
                                </ListItem>
                            </List>
                        </Space>
                    </Menu>
                }
                title={'Nastavení tabulky'}
            />
            <Button style="link" icon={<Icon fixedWidth icon={mcClose} />} onClick={() => setIsOpen(false)} />
        </ButtonToolbar>
    );
};
